import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr, BACKEND_URL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { AutoGlowBrand } from "./components/AutoGlowLogo";
import ThemeSwitch from "./components/ThemeSwitch";
import LangSwitcher from "./components/LangSwitcher";
import { getLocale, setLocale as persistLocale } from "../lib/i18n";
import BrandAssetsCard from "./components/BrandAssetsCard";
import {
  Crown, Buildings, Users, ChartLineUp, Lightning, Trash, Pause, Play,
  Sparkle, MagnifyingGlass, SignOut, ChatCircleDots, Star, CurrencyEur, Plus, PencilSimple,
  Receipt, QrCode, DownloadSimple, FileArrowDown, Stack, List, X, PaperPlaneTilt, Rocket, Globe, ShieldCheck, MapPin,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import QrGenerator from "./components/QrGenerator";
import DirectoryPanel from "./superadmin/DirectoryPanel";
import FunnelTab from "./superadmin/FunnelTab";
import EventLog from "./founderOS/EventLog";
import { FEATURES } from "../lib/features";

function StatCard({ label, value, accent, icon: Icon, testid }) {
  return (
    <div className="ag-card p-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">{label}</div>
        {Icon && <Icon size={18} weight="duotone" className="text-[var(--ag-gold)]" />}
      </div>
      <div className={`mt-3 font-display font-bold text-3xl tracking-tighter tabular-nums ${accent || "text-[var(--ag-text)]"}`}>{value}</div>
    </div>
  );
}

const STATUS_BADGES = {
  active: { label: "Activo", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  trialing: { label: "Trial", cls: "bg-[var(--ag-gold)]/15 text-[var(--ag-gold)] border-[var(--ag-gold)]/30" },
  trial_expired: { label: "Trial expirado", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  past_due: { label: "Impago", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  lifetime: { label: "Lifetime", cls: "bg-[var(--ag-aqua)]/20 text-[var(--ag-aqua-soft)] border-[var(--ag-aqua)]/40" },
  suspended: { label: "Suspendido", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
  none: { label: "Sin plan", cls: "bg-zinc-500/15 text-[var(--ag-text-muted)] border-zinc-500/30" },
};

export default function SuperAdmin({ embedded = false, forceTab = null, tabsAllowed = null, showLegacyBanner = false } = {}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [overview, setOverview] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  // Tab inicial: forceTab (embed) > ?tab=... (deep-link) > "orgs"
  const initialTab = forceTab || searchParams.get("tab") || "orgs";
  const [tab, setTabRaw] = useState(initialTab);

  // Legacy alias: /admin?tab=content (sin handler en SA) → redirige al hogar canónico en /founder.
  // Convergencia Sprint Q Fase 4: el módulo Content vive ahora en /founder/growth?sub=content.
  useEffect(() => {
    if (!embedded && searchParams.get("tab") === "content") {
      navigate("/founder/growth?sub=content", { replace: true });
    }
  }, [embedded, searchParams, navigate]);
  const setTab = (k) => {
    setTabRaw(k);
    if (!embedded) {
      // Persistimos en URL solo en modo no-embebido (legacy /admin) para deep-link.
      const next = new URLSearchParams(searchParams);
      next.set("tab", k);
      setSearchParams(next, { replace: true });
    }
  };
  const [locale, setLoc] = useState(getLocale());
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { persistLocale(locale); }, [locale]);
  // Si cambian props externos (caso navegación entre secciones del Founder OS),
  // sincronizamos el tab.
  useEffect(() => {
    if (forceTab && forceTab !== tab) setTabRaw(forceTab);
  }, [forceTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async (qVal = "") => {
    setLoading(true);
    try {
      const params = qVal ? `?q=${encodeURIComponent(qVal)}` : "";
      const [ov, og, rv] = await Promise.all([
        api.get("/superadmin/overview"),
        api.get(`/superadmin/organizations${params}`),
        api.get("/superadmin/platform-reviews"),
      ]);
      setOverview(ov.data);
      setOrgs(og.data);
      setReviews(rv.data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(""); }, []);

  // Quick Win #7 — Toast celebratorio en el primer subscription_started del día.
  // Polling cada 60s al funnel. Guarda en sessionStorage el último count visto
  // para no duplicar notificaciones al refrescar el tab.
  // Skip cuando el componente está embebido (en Founder OS) para no duplicar polls.
  useEffect(() => {
    if (user?.role !== "superadmin") return;
    if (embedded) return;
    const SEEN_KEY = "ag:sa:subscription_started_count";
    let stop = false;

    const tick = async () => {
      try {
        const { data } = await api.get("/superadmin/funnel?days=30");
        if (stop) return;
        const stage = (data.stages || []).find((s) => s.id === "subscription_started");
        const current = stage?.count ?? 0;
        const prevRaw = sessionStorage.getItem(SEEN_KEY);
        const prev = prevRaw == null ? null : parseInt(prevRaw, 10);
        if (prev != null && current > prev) {
          const delta = current - prev;
          toast.success(
            delta === 1 ? "🎉 ¡Nueva suscripción activada!" : `🎉 ¡${delta} nuevas suscripciones activadas!`,
            {
              description: "Un negocio acaba de convertir Trial → Plan de pago. Abre la pestaña Funnel para ver el detalle.",
              duration: 12000,
            }
          );
        }
        sessionStorage.setItem(SEEN_KEY, String(current));
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { stop = true; clearInterval(id); };
  }, [user?.role]);

  const grant = async (org) => {
    if (!window.confirm(`¿Otorgar suscripción VITALICIA gratuita a ${org.name}?`)) return;
    try { await api.post(`/superadmin/organizations/${org.id}/lifetime`); toast.success("Lifetime concedido"); load(q); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const revoke = async (org) => {
    if (!window.confirm(`¿Revocar lifetime de ${org.name}? Volverá a estado 'trial_expired'.`)) return;
    try { await api.post(`/superadmin/organizations/${org.id}/revoke-lifetime`); toast.success("Lifetime revocado"); load(q); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const suspend = async (org) => {
    if (!window.confirm(`¿Suspender ${org.name}? Su acceso quedará bloqueado.`)) return;
    try { await api.post(`/superadmin/organizations/${org.id}/suspend`); toast.success("Suspendido"); load(q); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const reactivate = async (org) => {
    try { await api.post(`/superadmin/organizations/${org.id}/reactivate`); toast.success("Reactivado"); load(q); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const deleteOrg = async (org) => {
    if (!window.confirm(`⚠ ELIMINAR ${org.name} y TODOS sus datos? Esta acción NO se puede deshacer.`)) return;
    if (!window.confirm("Confirma de nuevo: ¿borrar PERMANENTEMENTE?")) return;
    try { await api.delete(`/superadmin/organizations/${org.id}?confirm=true`); toast.success("Org eliminada"); load(q); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const deleteReview = async (r) => {
    if (!window.confirm("¿Borrar reseña?")) return;
    try { await api.delete(`/superadmin/platform-reviews/${r.id}`); toast.success("Reseña eliminada"); load(q); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div className={embedded ? "" : "min-h-screen bg-[var(--ag-midnight)] text-[var(--ag-text)]"}
         data-testid={embedded ? "superadmin-embedded" : undefined}>
      {/* Header SuperAdmin clásico · oculto en modo embebido (Founder OS provee el shell) */}
      {!embedded && (
      <header className="border-b border-[var(--ag-border)] bg-[var(--ag-midnight-soft)] sticky top-0 z-30" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Link to="/admin" className="flex items-center gap-2 sm:gap-3 min-w-0 shrink">
            <AutoGlowBrand size={18} />
            <Badge className="bg-[var(--ag-gold)] text-[var(--ag-midnight)] hover:bg-[var(--ag-gold)] text-[9px] sm:text-[10px] uppercase tracking-wider shrink-0"><Crown size={10} weight="fill" className="mr-1" /> Super-Admin</Badge>
          </Link>

          {/* Desktop actions */}
          <div className="hidden md:flex items-center gap-3">
            <ThemeSwitch />
            <LangSwitcher locale={locale} onChange={setLoc} dark />
            <div className="text-right max-w-[180px] lg:max-w-[260px]">
              <div className="text-xs text-[var(--ag-text-dim)]">Sesión</div>
              <div className="text-sm font-medium truncate">{user?.name} · {user?.email}</div>
            </div>
            <Button variant="outline" size="sm" onClick={()=>{logout(); navigate("/login", {replace:true});}} className="border-[var(--ag-border)] bg-transparent text-[var(--ag-text)] hover:bg-[var(--ag-midnight)]" data-testid="sa-logout">
              <SignOut size={14} className="mr-1.5" /> Salir
            </Button>
          </div>

          {/* Mobile: drawer trigger — siempre accesible aunque colapsen otros */}
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button
                className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md border border-[var(--ag-border)] text-[var(--ag-text)]"
                aria-label="Abrir menú Super-Admin"
                data-testid="sa-menu-trigger"
              >
                <List size={18} weight="bold" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[320px] h-screen max-h-screen overflow-y-auto bg-[#1A1A1A] text-white border-l border-[var(--ag-border)] p-0 flex flex-col [&>button.absolute]:hidden" data-testid="sa-mobile-drawer">
              <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--ag-border)]">
                <div className="flex items-center gap-2">
                  <AutoGlowBrand size={16} />
                  <Badge className="bg-[var(--ag-gold)] text-[var(--ag-midnight)] hover:bg-[var(--ag-gold)] text-[9px] uppercase tracking-wider"><Crown size={10} weight="fill" className="mr-1" /> Admin</Badge>
                </div>
                <button onClick={()=>setMenuOpen(false)} className="w-9 h-9 grid place-items-center rounded-md hover:bg-white/5" aria-label="Cerrar menú" data-testid="sa-menu-close">
                  <X size={18} weight="bold" />
                </button>
              </div>
              <div className="px-5 py-4 border-b border-[var(--ag-border)]">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Sesión</div>
                <div className="text-sm font-medium truncate mt-1" data-testid="sa-mobile-session">{user?.name}</div>
                <div className="text-[11px] text-[var(--ag-text-dim)] truncate">{user?.email}</div>
              </div>
              <div className="flex-1 px-5 py-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Idioma</span>
                  <LangSwitcher locale={locale} onChange={setLoc} dark />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Tema</span>
                  <ThemeSwitch />
                </div>
              </div>
              <div className="border-t border-[var(--ag-border)] px-5 py-4">
                <Button
                  onClick={()=>{logout(); navigate("/login", {replace:true});}}
                  variant="outline"
                  className="w-full border-red-500/40 text-red-300 hover:bg-red-500/10 rounded-sm"
                  data-testid="sa-mobile-logout"
                >
                  <SignOut size={14} className="mr-1.5" /> Cerrar sesión
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>
      )}

      {/* Banner LEGACY · solo en /admin (showLegacyBanner=true) — anima al Founder a usar el nuevo panel */}
      {showLegacyBanner && !embedded && (
        <div className="border-b border-[var(--ag-gold)]/40 bg-[var(--ag-gold)]/8 text-[var(--ag-gold)]" data-testid="sa-legacy-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-3 text-xs sm:text-sm">
            <div className="flex items-center gap-2 min-w-0">
              <Lightning size={14} weight="fill" className="shrink-0" />
              <span className="truncate"><strong>Panel legacy.</strong> Accede al nuevo Founder Control Center para tener todo unificado.</span>
            </div>
            <Link to="/founder" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm bg-[var(--ag-gold)] text-[var(--ag-midnight)] font-semibold hover:opacity-90 shrink-0" data-testid="sa-legacy-banner-cta">
              Ir al Founder Control Center
            </Link>
          </div>
        </div>
      )}

      <div className={embedded ? "space-y-6 sm:space-y-8" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8"}>
        {/* Title · oculto en embed */}
        {!embedded && (
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[var(--ag-gold)]">Panel maestro</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-1">Dashboard AutoGlow</h1>
          <p className="text-[var(--ag-text-dim)] mt-2 text-sm sm:text-base">Gestiona toda la plataforma · suscripciones · métricas · moderación</p>
        </div>
        )}

        {/* Metrics · oculto en embed · Mock MRR ELIMINADO (Convergence Sprint Feb 2026) */}
        {!embedded && (loading || !overview ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">{["a","b","c"].map((k)=><Skeleton key={`sk-${k}`} className="h-28" />)}</div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard testid="sa-stat-orgs" label="Organizaciones" value={overview.totals.organizations} icon={Buildings} />
            <StatCard testid="sa-stat-users" label="Usuarios totales" value={overview.totals.users} icon={Users} />
            <StatCard testid="sa-stat-services" label="Servicios realizados" value={overview.totals.services} icon={Lightning} />
          </div>
        ))}

        {/* Subs breakdown · oculto en embed */}
        {!embedded && overview && (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {Object.entries(overview.subscriptions).map(([k, v]) => {
              const b = STATUS_BADGES[k] || STATUS_BADGES.none;
              return (
                <div key={k} className={`ag-card p-3 border ${b.cls}`}>
                  <div className="text-[9px] uppercase tracking-wider opacity-80">{b.label}</div>
                  <div className="font-display font-bold text-xl tabular-nums">{v}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Tabs · Sprint H Batch 9.1 — mobile dropdown + desktop scroll horizontal
            Convergence Sprint: soporta `forceTab` (oculta tabs row · útil en Founder OS)
            y `tabsAllowed` (filtra qué tabs son visibles). */}
        {(() => {
          const ALL_TABS = [
            { key: "orgs",     label: "Organizaciones",                Icon: Buildings },
            { key: "directory", label: "Directorio",                   Icon: MapPin },
            { key: "funnel",   label: "Funnel · Validación",           Icon: ChartLineUp },
            { key: "north-star", label: "★ Estrella del Norte",        Icon: Crown },
            { key: "roadmap",  label: "Roadmap",                       Icon: Stack },
            FEATURES.SUPERADMIN_AI_INSIGHTS && { key: "ai-insights", label: "IA Insights", Icon: Lightning },
            { key: "churn",    label: "Riesgo churn",                  Icon: Pause },
            { key: "saas-metrics", label: "SaaS Metrics",              Icon: ChartLineUp },
            { key: "nps-dashboard", label: "NPS global",               Icon: ChatCircleDots },
            FEATURES.SUPERADMIN_AB_TESTING && { key: "ab-results", label: "A/B testing", Icon: Lightning },
            FEATURES.COUPONS && { key: "coupons",  label: "Cupones",                       Icon: Sparkle },
            FEATURES.AFFILIATES_B2C && { key: "affiliates", label: "Afiliados",            Icon: Users },
            { key: "reviews",  label: `Reseñas sobre AutoGlow (${reviews.length})`, Icon: ChatCircleDots },
            { key: "pricing",  label: "Tarifas internacionales",       Icon: CurrencyEur },
            { key: "brand",    label: "Logo & Marca",                  Icon: Sparkle },
            { key: "invoices", label: "Facturas SaaS",                 Icon: Receipt },
            { key: "qr",       label: "Herramientas / QR",             Icon: QrCode },
            { key: "catalog",  label: "Catálogo Maestro",              Icon: Stack },
            { key: "audit",    label: "Event Log",                     Icon: ShieldCheck },
          ].filter(Boolean);
          const TABS = tabsAllowed && tabsAllowed.length
            ? ALL_TABS.filter((t) => tabsAllowed.includes(t.key))
            : ALL_TABS;
          // Si forceTab está, no mostramos tabs row (la sección de Founder OS provee sus propios L2 tabs).
          if (forceTab) return null;
          return (
            <>
              {/* MOBILE: select dropdown — todas las pestañas accesibles en un toque */}
              <div className="md:hidden border-b border-[var(--ag-border)] pb-3" data-testid="sa-tabs-mobile-select-wrap">
                <label htmlFor="sa-tabs-mobile-select" className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Sección del panel</label>
                <select
                  id="sa-tabs-mobile-select"
                  value={tab}
                  onChange={(e)=>setTab(e.target.value)}
                  data-testid="sa-tabs-mobile-select"
                  className="mt-1.5 w-full h-11 px-3 rounded-sm bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-[var(--ag-gold)]"
                >
                  {TABS.map((t) => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </select>
              </div>

              {/* DESKTOP: tabs horizontales */}
              <div className="hidden md:flex items-center gap-2 border-b border-[var(--ag-border)] overflow-x-auto whitespace-nowrap -mx-1 px-1" data-testid="sa-tabs-scroll">
                {TABS.map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    onClick={()=>setTab(key)}
                    className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab===key?"border-[var(--ag-gold)] text-[var(--ag-text)]":"border-transparent text-[var(--ag-text-dim)] hover:text-white"}`}
                    data-testid={`sa-tab-${key}`}
                  >
                    <Icon size={14} className="inline mr-1.5" /> {label}
                  </button>
                ))}
              </div>
            </>
          );
        })()}

        {tab === "brand" && <BrandAssetsCard />}
        {tab === "directory" && <DirectoryPanel />}
        {tab === "funnel" && <FunnelTab />}
        {tab === "pricing" && <PricingTab />}
        {tab === "invoices" && <SaasInvoicesTab />}
        {tab === "qr" && <QrToolsTab />}
        {tab === "catalog" && <MasterCatalogTab />}
        {tab === "audit" && <AuditLogTab />}
        {tab === "ai-insights" && <AIInsightsTab />}
        {tab === "churn" && <ChurnRiskTab />}
        {tab === "saas-metrics" && <SaasMetricsTab />}
        {tab === "coupons" && <CouponsTab />}
        {tab === "affiliates" && <AffiliatesTab />}
        {tab === "north-star" && <NorthStarTab />}
        {tab === "nps-dashboard" && <NpsDashboardTab />}
        {tab === "ab-results" && <AbResultsTab />}
        {tab === "roadmap" && <RoadmapTab />}

        {/* ORGS tab */}
        {tab === "orgs" && (
          <>
            <form onSubmit={(e)=>{e.preventDefault(); load(q);}} className="flex items-center gap-2">
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-dim)]" />
                <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Buscar por nombre, slug, NIF, ciudad…" className="pl-9 h-10" data-testid="sa-search" />
              </div>
              <Button type="submit" className="ag-btn-gold h-10 px-5" data-testid="sa-search-btn">Buscar</Button>
            </form>

            <div className="ag-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--ag-border)] bg-[var(--ag-midnight-soft)]">
                      <th className="text-left text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Centro</th>
                      <th className="text-left text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Admin</th>
                      <th className="text-left text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Ciudad</th>
                      <th className="text-center text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Colab.</th>
                      <th className="text-center text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Servicios</th>
                      <th className="text-left text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Plan</th>
                      <th className="text-right text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgs.map((o) => {
                      const status = o.subscription_status || "none";
                      const badge = STATUS_BADGES[status] || STATUS_BADGES.none;
                      return (
                        <tr key={o.id} className="border-b border-[var(--ag-border)] hover:bg-[var(--ag-midnight-soft)]/50 transition-colors" data-testid={`sa-org-${o.id}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {o.logo_url ? (
                                <img src={o.logo_url} alt="" className="w-8 h-8 rounded bg-white object-contain p-0.5" />
                              ) : (
                                <div className="w-8 h-8 rounded bg-[var(--ag-gold)] text-[var(--ag-midnight)] grid place-items-center font-display font-black text-xs">
                                  {o.name?.charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div className="min-w-0">
                                <div className="font-medium truncate">{o.name}</div>
                                <div className="text-[10px] text-[var(--ag-text-dim)] font-mono-tab">/{o.slug}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-[var(--ag-text-dim)]">
                            <div>{o.admin_name || "—"}</div>
                            <div className="text-[10px]">{o.admin_email || "—"}</div>
                          </td>
                          <td className="px-4 py-3 text-[var(--ag-text-dim)]">{o.city || "—"}</td>
                          <td className="px-4 py-3 text-center tabular-nums">{o.colaborador_count}</td>
                          <td className="px-4 py-3 text-center tabular-nums">{o.services_count}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${badge.cls}`}>{badge.label}</span>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="inline-flex gap-1">
                              {status === "lifetime" ? (
                                <Button size="sm" variant="outline" onClick={()=>revoke(o)} className="border-[var(--ag-border)] bg-transparent text-[var(--ag-text)] h-8 text-xs" data-testid={`sa-revoke-${o.id}`}>Revocar</Button>
                              ) : (
                                <Button size="sm" onClick={()=>grant(o)} className="bg-[var(--ag-aqua)] hover:bg-[var(--ag-aqua-soft)] h-8 text-xs" data-testid={`sa-grant-${o.id}`}>
                                  <Sparkle size={11} weight="fill" className="mr-1" /> Lifetime
                                </Button>
                              )}
                              {status === "suspended" ? (
                                <Button size="icon" variant="outline" onClick={()=>reactivate(o)} className="border-[var(--ag-border)] bg-transparent h-8 w-8" title="Reactivar" data-testid={`sa-reactivate-${o.id}`}><Play size={12} /></Button>
                              ) : (
                                <Button size="icon" variant="outline" onClick={()=>suspend(o)} className="border-[var(--ag-border)] bg-transparent h-8 w-8" title="Suspender" data-testid={`sa-suspend-${o.id}`}><Pause size={12} /></Button>
                              )}
                              <Button size="icon" variant="ghost" onClick={()=>deleteOrg(o)} className="h-8 w-8 hover:bg-red-500/10" title="Eliminar" data-testid={`sa-delete-${o.id}`}><Trash size={12} className="text-red-400" /></Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {orgs.length === 0 && (
                      <tr><td colSpan={7} className="text-center text-[var(--ag-text-dim)] py-12">No hay organizaciones.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* REVIEWS tab */}
        {tab === "reviews" && (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto overflow-x-hidden pr-1" data-testid="sa-reviews-scroll">
            {reviews.length === 0 ? (
              <div className="ag-card p-12 text-center">
                <Star size={48} weight="duotone" className="text-[var(--ag-text-dim)] mx-auto" />
                <h3 className="font-display font-bold text-lg mt-4">Sin reseñas todavía</h3>
                <p className="text-sm text-[var(--ag-text-dim)] mt-2">Cuando los usuarios opinen sobre AutoGlow, aparecerán aquí.</p>
              </div>
            ) : reviews.map((r) => (
              <div key={r.id} className="ag-card p-5" data-testid={`sa-review-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-1">
                      {[0,1,2,3,4].map((i) => (
                        <Star key={`s-${r.id}-${i}`} size={14} weight={i < r.rating ? "fill" : "regular"} className={i < r.rating ? "text-[var(--ag-gold)]" : "text-[var(--ag-border)]"} />
                      ))}
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">{new Date(r.created_at).toLocaleDateString("es-ES")}</span>
                    </div>
                    {r.title && <div className="font-display font-bold mt-2">{r.title}</div>}
                    <p className="text-sm text-[var(--ag-text-dim)] mt-1">{r.body}</p>
                    <div className="text-[11px] text-[var(--ag-text-dim)] mt-2">— {r.author_name || r.author_email || "Anónimo"}</div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={()=>deleteReview(r)} className="hover:bg-red-500/10"><Trash size={14} className="text-red-400" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PricingTab() {
  const EMPTY = { country_code: "", country_name: "", currency: "EUR", price_cents: 2900, stripe_price_id: "", active: true };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/superadmin/pricing"); setItems(data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({ country_code: p.country_code, country_name: p.country_name, currency: p.currency,
              price_cents: p.price_cents, stripe_price_id: p.stripe_price_id, active: p.active });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.country_code || form.country_code.length !== 2) { toast.error("Código ISO de 2 letras"); return; }
    if (!form.stripe_price_id) { toast.error("Stripe Price ID obligatorio"); return; }
    try {
      const payload = { ...form, country_code: form.country_code.toUpperCase(), currency: form.currency.toUpperCase(), price_cents: Number(form.price_cents) };
      if (editingId) await api.put(`/superadmin/pricing/${editingId}`, payload);
      else await api.post("/superadmin/pricing", payload);
      toast.success(editingId ? "Tarifa actualizada" : "Tarifa creada");
      setOpen(false); setEditingId(null); setForm(EMPTY); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar tarifa?")) return;
    try { await api.delete(`/superadmin/pricing/${id}`); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-xs text-[var(--ag-text-dim)]">
          Tarifa por país. Al registrarse una org se aplica automáticamente según su <code>country</code>. Sin coincidencia → fallback EUR 29€.
        </p>
        <Button onClick={startCreate} className="ag-btn-gold h-9" data-testid="sa-pricing-new">
          <Plus size={13} className="mr-1.5" /> Nueva tarifa
        </Button>
      </div>
      <div className="ag-card overflow-hidden">
        <div className="overflow-x-auto" data-testid="sa-pricing-scroll">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[var(--ag-midnight-soft)]">
            <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)]">
              <th className="px-4 py-3">País</th>
              <th className="px-4 py-3">Divisa</th>
              <th className="px-4 py-3 text-right">Precio</th>
              <th className="px-4 py-3">Stripe Price ID</th>
              <th className="px-4 py-3 text-center">Activo</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-[var(--ag-text-dim)] py-8">Sin tarifas — añade la primera</td></tr>}
            {items.map((p, idx) => (
              <tr key={`${p.id || p.country_code}-${idx}`} className="border-t border-[var(--ag-border)]" data-testid={`sa-pricing-row-${p.country_code}`}>
                <td className="px-4 py-3"><span className="font-mono-tab text-[var(--ag-gold)] mr-2">{p.country_code}</span>{p.country_name}</td>
                <td className="px-4 py-3 font-mono-tab">{p.currency}</td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold">{(p.price_cents/100).toFixed(2)} {p.currency === "GBP" ? "£" : p.currency === "USD" ? "$" : "€"}</td>
                <td className="px-4 py-3 font-mono-tab text-[10px] text-[var(--ag-text-dim)]">{p.stripe_price_id}</td>
                <td className="px-4 py-3 text-center">{p.active ? <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30">ON</Badge> : <Badge variant="secondary">OFF</Badge>}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => startEdit(p)}><PencilSimple size={14} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(p.id)}><Trash size={14} className="text-red-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-[var(--ag-midnight-card)] border border-[var(--ag-border)] rounded-md p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="sa-pricing-dialog">
            <div className="text-xl font-display font-bold tracking-tighter mb-4">{editingId ? "Editar tarifa" : "Nueva tarifa"}</div>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">ISO-2</label>
                  <Input value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value.toUpperCase().slice(0, 2) })} className="mt-1 font-mono-tab uppercase" placeholder="ES" data-testid="sa-pricing-cc" />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Nombre del país</label>
                  <Input value={form.country_name} onChange={(e) => setForm({ ...form, country_name: e.target.value })} className="mt-1" placeholder="España" data-testid="sa-pricing-name" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Divisa</label>
                  <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="w-full mt-1 px-3 py-2 bg-transparent border rounded font-mono-tab" data-testid="sa-pricing-cur">
                    {["EUR", "GBP", "USD", "MXN", "BRL", "CHF"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Precio (céntimos)</label>
                  <Input type="number" value={form.price_cents} onChange={(e) => setForm({ ...form, price_cents: e.target.value })} className="mt-1 tabular-nums" data-testid="sa-pricing-price" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Stripe Price ID</label>
                <Input value={form.stripe_price_id} onChange={(e) => setForm({ ...form, stripe_price_id: e.target.value })} className="mt-1 font-mono-tab" placeholder="price_..." data-testid="sa-pricing-stripeid" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[var(--ag-gold)]" />
                Plan activo
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={submit} className="ag-btn-gold" data-testid="sa-pricing-submit">{editingId ? "Actualizar" : "Crear"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



// ============================================================================
// SaaS Invoices Tab — gestión y exportación lote facturas AG-YYYY-XXXX
// ============================================================================
function SaasInvoicesTab() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [country, setCountry] = useState("");
  const [plan, setPlan] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (year) params.set("year", year);
      if (country) params.set("country", country.toUpperCase());
      if (plan) params.set("plan", plan);
      const { data } = await api.get(`/superadmin/saas-invoices?${params}`);
      setData(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadOne = (invNum) => {
    const url = `${BACKEND_URL}/api/superadmin/saas-invoices/${invNum}/pdf`;
    fetch(url, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = `${invNum}.pdf`; a.click();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast.error("No se pudo descargar el PDF"));
  };

  const resendOne = async (invNum) => {
    try {
      const { data } = await api.post(`/superadmin/saas-invoices/${invNum}/resend`);
      toast.success(`Factura reenviada a ${data.to || "el admin del centro"}`);
      load();
    } catch (err) {
      toast.error(formatErr(err) || "No se pudo reenviar el email");
    }
  };

  const exportBatch = (fmt) => {
    const params = new URLSearchParams();
    if (year) params.set("year", year);
    if (country) params.set("country", country.toUpperCase());
    if (plan) params.set("plan", plan);
    const url = `${BACKEND_URL}/api/superadmin/saas-invoices/export/${fmt}?${params}`;
    fetch(url, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = `AutoGlow-SaaS-${year || "all"}.${fmt}`;
        a.click();
        URL.revokeObjectURL(u);
        toast.success(`Exportado ${fmt.toUpperCase()}`);
      })
      .catch(() => toast.error("No se pudo exportar"));
  };

  return (
    <div className="space-y-5" data-testid="saas-invoices-tab">
      <div className="ag-card p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Año</label>
            <Input type="number" value={year} onChange={(e)=>setYear(e.target.value)} className="mt-1 w-28 tabular-nums" data-testid="inv-filter-year" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">País (código)</label>
            <Input value={country} onChange={(e)=>setCountry(e.target.value)} placeholder="ES" className="mt-1 w-24 uppercase font-mono-tab" data-testid="inv-filter-country" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Plan</label>
            <Input value={plan} onChange={(e)=>setPlan(e.target.value)} placeholder="agenda_gestion" className="mt-1 w-44 font-mono-tab text-xs" data-testid="inv-filter-plan" />
          </div>
          <Button onClick={load} className="ag-btn-gold h-10" data-testid="inv-search-btn">
            <MagnifyingGlass size={14} className="mr-1.5" /> Filtrar
          </Button>
          <div className="flex gap-2 ml-auto">
            <Button onClick={()=>exportBatch("csv")} variant="outline" className="h-10 rounded-sm border-[var(--ag-border)]" data-testid="inv-export-csv">
              <FileArrowDown size={14} className="mr-1.5" /> Export CSV
            </Button>
            <Button onClick={()=>exportBatch("zip")} className="ag-btn-aqua h-10 rounded-sm" data-testid="inv-export-zip">
              <DownloadSimple size={14} className="mr-1.5" /> Export ZIP (PDFs)
            </Button>
            <Button
              onClick={async () => {
                const keepStr = window.prompt(
                  "Reset contador SaaS · ¿Cuántas facturas mantener?\n\n" +
                  "Las facturas más antiguas se conservan. Las posteriores se borran.\n" +
                  "Escribe 0 para BORRAR TODAS y empezar desde AG-YYYY-0001.\n\n" +
                  "(Acción irreversible)",
                  "0"
                );
                if (keepStr === null) return;
                const keep = parseInt(keepStr, 10);
                if (isNaN(keep) || keep < 0) { toast.error("Número inválido"); return; }
                if (!window.confirm(`Vas a BORRAR todas las facturas excepto las primeras ${keep} y resetear el contador.\n\n¿Confirmar?`)) return;
                try {
                  const { data } = await api.post("/superadmin/saas-invoices/reset-counter", { keep_real_count: keep, reset_to_seq: keep });
                  toast.success(`✓ Borradas ${data.deleted} · Contador ahora en ${data.next_invoice_preview}`);
                  load();
                } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Error reseteando"); }
              }}
              variant="outline"
              className="h-10 rounded-sm border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20"
              data-testid="inv-reset-counter"
              title="Borrar facturas de prueba y resetear contador AG-YYYY-XXXX"
            >
              ⚠ Reset contador
            </Button>
          </div>
        </div>
      </div>

      {/* Resumen */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard label="Facturas listadas" value={data.count} icon={Receipt} testid="inv-stat-count" />
          <StatCard label="Importe total (EUR)" value={`${data.total_eur.toFixed(2)} €`} accent="text-[var(--ag-gold)]" icon={CurrencyEur} testid="inv-stat-total" />
          <StatCard label="Serie" value={`AG-${year}-XXXX`} icon={Sparkle} testid="inv-stat-serie" />
        </div>
      )}

      {/* Tabla */}
      <div className="ag-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="inv-table">
            <thead>
              <tr className="border-b border-[var(--ag-border)] bg-[var(--ag-midnight-soft)]">
                <th className="text-left text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Nº Factura</th>
                <th className="text-left text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Centro</th>
                <th className="text-left text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">NIF</th>
                <th className="text-left text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Plan</th>
                <th className="text-right text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Importe</th>
                <th className="text-center text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">País</th>
                <th className="text-center text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">IVA</th>
                <th className="text-left text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">Periodo</th>
                <th className="text-right text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] px-4 py-3">PDF / Email</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="text-center py-8 text-[var(--ag-text-dim)]">Cargando…</td></tr>
              )}
              {!loading && (!data?.invoices || data.invoices.length === 0) && (
                <tr><td colSpan={9} className="text-center py-8 text-[var(--ag-text-dim)]">Sin facturas para los filtros aplicados.</td></tr>
              )}
              {(data?.invoices || []).map((inv, idx) => (
                <tr key={`${inv.id}-${idx}`} className="border-b border-[var(--ag-border)] hover:bg-[var(--ag-midnight-soft)]/40" data-testid={`inv-row-${inv.id}`}>
                  <td className="px-4 py-3 font-mono-tab text-[var(--ag-gold)]">{inv.id}</td>
                  <td className="px-4 py-3">{inv.organization_name}</td>
                  <td className="px-4 py-3 text-xs text-[var(--ag-text-dim)]">{inv.organization_nif || "—"}</td>
                  <td className="px-4 py-3 text-xs">{inv.plan_display || inv.plan_id}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold">{Number(inv.amount_eur || 0).toFixed(2)} €</td>
                  <td className="px-4 py-3 text-center text-xs font-mono-tab">{inv.country_code || "—"}</td>
                  <td className="px-4 py-3 text-center text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">{inv.vat_policy || "—"}</td>
                  <td className="px-4 py-3 text-xs text-[var(--ag-text-dim)]">{inv.period_start} → {inv.period_end}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline" onClick={()=>downloadOne(inv.id)} className="rounded-sm h-8 px-2 border-[var(--ag-border)]" title="Descargar PDF" data-testid={`inv-pdf-${inv.id}`}>
                        <DownloadSimple size={12} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={()=>resendOne(inv.id)} className="rounded-sm h-8 px-2 border-[var(--ag-border)] hover:border-[var(--ag-gold)] hover:text-[var(--ag-gold)]" title="Reenviar por email al admin del centro" data-testid={`inv-resend-${inv.id}`}>
                        <PaperPlaneTilt size={12} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// QR Tools Tab — generación masiva para marketing
// ============================================================================
function QrToolsTab() {
  const [orgs, setOrgs] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [customUrl] = useState(`${window.location.origin}/marketplace`);

  useEffect(() => {
    api.get("/superadmin/organizations?limit=500")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : (data.organizations || []);
        setOrgs(list.filter((o) => o.slug));
      })
      .catch(() => {});
  }, []);

  const selectedOrg = orgs.find((o) => o.slug === selectedSlug);
  const orgUrl = selectedOrg
    ? `${window.location.origin}/reservar/${selectedOrg.slug}`
    : "";

  return (
    <div className="space-y-6" data-testid="qr-tools-tab">
      {/* QR del Marketplace global */}
      <QrGenerator
        defaultUrl={customUrl}
        title="QR del Marketplace AutoGlow"
        subtitle="URL pública del directorio global. Imprímelo en ferias, presentaciones o cartelería corporativa."
        brandColor="#0033A0"
        allowEdit
        hiResSize={2048}
        filenameBase="autoglow-marketplace"
      />

      {/* QR por taller (selector) */}
      <div className="ag-card p-6">
        <div className="flex items-center gap-2 mb-3">
          <Buildings size={18} weight="duotone" className="text-[var(--ag-gold)]" />
          <h3 className="font-display text-xl font-bold tracking-tighter">QR por centro</h3>
        </div>
        <p className="text-xs text-[var(--ag-text-dim)] mb-4">
          Selecciona un taller para generar su QR personalizado con el color corporativo
          configurado en sus ajustes. Útil para entregárselo en alta resolución.
        </p>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Centro</label>
          <select
            value={selectedSlug}
            onChange={(e)=>setSelectedSlug(e.target.value)}
            className="mt-1 w-full h-10 px-3 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm"
            data-testid="qr-org-selector"
          >
            <option value="">— Elige un centro —</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.slug}>{o.name} ({o.slug})</option>
            ))}
          </select>
        </div>
        {selectedOrg && (
          <div className="mt-5">
            <QrGenerator
              defaultUrl={orgUrl}
              title={`QR · ${selectedOrg.name}`}
              subtitle="URL pública del centro · color corporativo del centro aplicado."
              brandColor={selectedOrg.brand_color || "#0033A0"}
              allowEdit={false}
              hiResSize={2048}
              filenameBase={`autoglow-${selectedOrg.slug}`}
            />
          </div>
        )}
      </div>

      {/* Sprint S-1 · #23 · Publicación masiva SEO */}
      <SeoAutoPublishPanel />

      {/* B-001 · Geocodificación masiva (Nominatim OSM) */}
      <GeocodeBulkPanel />
    </div>
  );
}


/**
 * Sprint S-1 · #23 · Auto-publish SEO bulk.
 */
function SeoAutoPublishPanel() {
  const [dryResult, setDryResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState({
    min_catalog_items: 3,
    require_opening_hours: true,
    exclude_test_names: true,
  });

  const run = async (dry_run) => {
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/orgs/auto-publish", {
        ...opts, dry_run,
      });
      setDryResult(data);
      if (dry_run) {
        toast.success(`${data.candidates_count} centros listos para publicar`);
      } else {
        toast.success(`✓ ${data.published_count} centros publicados`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error en la operación");
    } finally { setBusy(false); }
  };

  return (
    <div className="ag-card p-6" data-testid="seo-auto-publish-panel">
      <div className="flex items-center gap-2 mb-3">
        <Globe size={18} weight="duotone" className="text-[var(--ag-gold)]" />
        <h3 className="font-display text-xl font-bold tracking-tighter">Publicación masiva SEO</h3>
      </div>
      <p className="text-xs text-[var(--ag-text-dim)] mb-4">
        Activa <span className="font-mono">is_public_org=true</span> en lote para todas las orgs reales
        que cumplen los criterios. Tras ejecutarlo aparecerán en <code>/marketplace</code> y serán
        indexables por Google (sitemap + ficha pública + JSON-LD).
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <label className="text-xs flex items-center gap-2">
          <span className="opacity-70 w-32 shrink-0">Servicios mínimos</span>
          <input type="number" min="1" max="50" value={opts.min_catalog_items}
            onChange={(e) => setOpts((o) => ({ ...o, min_catalog_items: Number(e.target.value) || 1 }))}
            className="w-16 h-9 px-2 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm"
            data-testid="seo-min-catalog" />
        </label>
        <label className="text-xs flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={opts.require_opening_hours}
            onChange={(e) => setOpts((o) => ({ ...o, require_opening_hours: e.target.checked }))}
            data-testid="seo-require-hours" />
          <span className="opacity-90">Exigir horarios</span>
        </label>
        <label className="text-xs flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={opts.exclude_test_names}
            onChange={(e) => setOpts((o) => ({ ...o, exclude_test_names: e.target.checked }))}
            data-testid="seo-exclude-test" />
          <span className="opacity-90">Excluir test/seed</span>
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => run(true)} disabled={busy}
          className="px-4 py-2 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm hover:border-[var(--ag-gold)] disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="seo-dry-run-btn">
          <List size={14} weight="bold" /> Previsualizar (dry-run)
        </button>
        <button onClick={() => run(false)} disabled={busy || !dryResult || dryResult.candidates_count === 0}
          className="px-4 py-2 rounded-sm bg-amber-400 hover:bg-amber-500 text-[#1A1A1A] font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="seo-publish-btn"
          title={!dryResult ? "Primero haz un dry-run" : ""}>
          <Rocket size={14} weight="fill" /> Publicar {dryResult ? dryResult.candidates_count : ""} ahora
        </button>
      </div>

      {dryResult && (
        <div className="mt-5 border-t border-[var(--ag-border)] pt-4" data-testid="seo-result-panel">
          {dryResult.dry_run ? (
            <p className="text-xs text-amber-300 mb-2">
              📋 Dry-run · <span className="font-bold">{dryResult.candidates_count}</span> centros candidatos
              {dryResult.candidates_count === 0 && " — ningún centro cumple los criterios. Relaja los filtros o añade catálogo/horarios a tus centros."}
            </p>
          ) : (
            <p className="text-xs text-emerald-300 mb-2">
              ✅ Publicados <span className="font-bold">{dryResult.published_count}</span> centros. Ya son visibles en /marketplace e indexables.
            </p>
          )}
          {dryResult.candidates?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
              {dryResult.candidates.map((c) => (
                <div key={c.id} className="text-xs px-3 py-2 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] flex items-center justify-between" data-testid={`seo-candidate-${c.slug}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-[var(--ag-text)] truncate">{c.name}</div>
                    <div className="text-[10px] opacity-60 font-mono">/{c.slug}</div>
                  </div>
                  <span className="text-[10px] opacity-60 shrink-0">{c.catalog_count} servicios</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * B-001 · Geocodificación masiva con Nominatim (OSM) — coords lat/lng para Map view del Marketplace.
 * Procesa hasta `max_items` orgs públicas, no demo, con address pero sin coords.
 * Respeta TOS de Nominatim (1 req/s).
 */
function GeocodeBulkPanel() {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [maxItems, setMaxItems] = useState(30);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(`/superadmin/orgs/geocode-bulk?max_items=${maxItems}`);
      setResult(data);
      if (data.geocoded > 0) {
        toast.success(`✓ ${data.geocoded} centros geocodificados`);
      } else if (data.processed === 0) {
        toast.info("No hay centros pendientes de geocodificar");
      } else {
        toast.warning(`${data.processed} procesados, ninguno encontrado en Nominatim`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al geocodificar");
    } finally { setBusy(false); }
  };

  return (
    <div className="ag-card p-6" data-testid="geocode-bulk-panel">
      <div className="flex items-center gap-2 mb-3">
        <MapPin size={18} weight="duotone" className="text-[var(--ag-gold)]" />
        <h3 className="font-display text-xl font-bold tracking-tighter">Geocodificación masiva</h3>
      </div>
      <p className="text-xs text-[var(--ag-text-dim)] mb-4">
        Asigna coordenadas <span className="font-mono">lat/lng</span> a las orgs que tienen dirección pero no están en el mapa.
        Usa Nominatim (OpenStreetMap, gratis). Throttle 1 req/s — un lote de 30 tarda ~30s. Tras ejecutarlo, los centros aparecen como pines en la vista <em>Mapa</em> del Marketplace.
      </p>

      <div className="flex items-center gap-2 mb-4">
        <label className="text-xs flex items-center gap-2">
          <span className="opacity-70 shrink-0">Máx. por lote</span>
          <input type="number" min="1" max="100" value={maxItems}
            onChange={(e) => setMaxItems(Number(e.target.value) || 30)}
            className="w-20 h-9 px-2 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm"
            data-testid="geocode-max-items" />
        </label>
        <Button onClick={run} disabled={busy} size="sm" className="ag-btn-gold rounded-sm h-9" data-testid="geocode-run">
          {busy ? "Geocodificando…" : "Ejecutar geocoding"}
        </Button>
      </div>

      {result && (
        <div className="border-t border-[var(--ag-border)] pt-4 mt-2" data-testid="geocode-result">
          <p className="text-xs text-emerald-300 mb-2">
            ✅ Procesados <span className="font-bold">{result.processed}</span> · Geocodificados <span className="font-bold">{result.geocoded}</span>
          </p>
          {result.items?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
              {result.items.map((it) => (
                <div key={it.id} className="text-xs px-3 py-2 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)]" data-testid={`geocode-item-${it.id}`}>
                  <div className="font-semibold text-[var(--ag-text)] truncate">{it.name}</div>
                  <div className="text-[10px] opacity-60 font-mono tabular-nums">
                    {Number(it.lat).toFixed(5)}, {Number(it.lng).toFixed(5)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}



// =======================================================================
// CATÁLOGO MAESTRO · Sprint H Batch 3
// Vista global de los 15 sub-sectores del Ecosystem, contando cuántos
// centros operan en cada uno. Sin acción de edición — es el master ref.
// =======================================================================
function MasterCatalogTab() {
  const [sectors, setSectors] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const [s, o] = await Promise.all([
          api.get("/catalog/sectors"),
          api.get("/superadmin/organizations"),
        ]);
        setSectors(s.data.sectors || []);
        setOrgs(o.data || []);
      } finally { setLoading(false); }
    })();
  }, []);
  const countFor = (sid) => orgs.filter((o) => (o.sectors || []).includes(sid)).length;
  return (
    <div className="space-y-5" data-testid="sa-master-catalog-tab">
      <div className="ag-card p-5">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ag-gold)]">Catálogo Maestro</div>
        <h2 className="font-display font-bold text-2xl tracking-tighter mt-1">15 subsectores del Ecosistema</h2>
        <p className="text-sm text-[var(--ag-text-dim)] mt-1">Cada centro elige uno o varios al registrarse. Define su tarjeta visual en Marketplace y su plantilla de servicios.</p>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1,2,3,4,5,6].map((k) => <Skeleton key={k} className="h-20" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {sectors.map((s) => (
            <div key={s.id} className="ag-card p-4 flex items-center gap-3" data-testid={`sa-catalog-${s.id}`}>
              <div className="w-10 h-10 rounded-sm grid place-items-center bg-[var(--ag-gold)]/15 text-[var(--ag-gold)] shrink-0">
                <Stack size={18} weight="duotone" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-base text-[var(--ag-text)] truncate">{s.label_es}</div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] mt-0.5">{s.id}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display font-black text-2xl tabular-nums text-[var(--ag-text)]">{countFor(s.id)}</div>
                <div className="text-[9px] uppercase tracking-wider text-[var(--ag-text-dim)]">centros</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// =======================================================================
// AUDIT LOG · Sprint S-4 · #28
// =======================================================================
function AuditLogTab() {
  // Fase 7 · extracción: el cuerpo vive ahora en /app/frontend/src/pages/founderOS/EventLog.jsx
  // Wrapper preservado solo para mantener compatibilidad con tab === 'audit' del SA legacy.
  return <EventLog />;
}


// =======================================================================
// Sprint S-6 · IA Insights tab
// =======================================================================
function AIInsightsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = async (d) => {
    setLoading(true);
    try {
      const r = await api.get(`/superadmin/ai-insights?days=${d || days}`);
      setData(r.data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  if (loading) return <div className="p-6 text-sm text-[var(--ag-text-dim)]">Cargando insights…</div>;
  if (!data) return <div className="p-6 text-sm text-[var(--ag-text-dim)]">Sin datos.</div>;
  const T = data.totals || {};
  const maxIntent = Math.max(1, ...(data.top_intents || []).map((i) => i.count));

  return (
    <div className="space-y-5" data-testid="ai-insights-tab">
      <div className="ag-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Lightning size={18} weight="duotone" className="text-[var(--ag-gold)]" />
            <h3 className="font-display text-xl font-bold tracking-tighter">IA Insights</h3>
          </div>
          <select value={days} onChange={(e)=>setDays(Number(e.target.value))}
            className="h-9 px-2 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm"
            data-testid="ai-insights-days">
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
          </select>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Mensajes totales" value={T.messages} icon={ChatCircleDots} testid="ins-total-msgs" />
          <StatCard label="Sesiones únicas" value={T.sessions} icon={Users} testid="ins-sessions" />
          <StatCard label="Handoffs IA→reserva" value={T.handoffs} icon={Sparkle} testid="ins-handoffs" />
          <StatCard label="Conversión IA" value={`${T.conversion_rate_pct || 0}%`} icon={ChartLineUp} accent="text-[var(--ag-gold)]" testid="ins-conv-rate" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="ag-card p-5">
          <h4 className="font-display text-base font-bold mb-3 tracking-tight">Top intents</h4>
          {(data.top_intents || []).length === 0 && (
            <div className="text-sm text-[var(--ag-text-dim)]">Sin datos en el periodo.</div>
          )}
          <div className="space-y-2" data-testid="ai-insights-top-intents">
            {(data.top_intents || []).map((i) => (
              <div key={i.label} className="flex items-center gap-3 text-sm" data-testid={`intent-${i.label}`}>
                <div className="w-20 text-[var(--ag-text-dim)] capitalize">{i.label}</div>
                <div className="flex-1 h-2 rounded-sm bg-[var(--ag-midnight)] overflow-hidden">
                  <div className="h-full bg-[var(--ag-gold)]" style={{ width: `${(i.count / maxIntent) * 100}%` }} />
                </div>
                <div className="w-10 text-right font-mono tabular-nums text-[var(--ag-text)]">{i.count}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="ag-card p-5">
          <h4 className="font-display text-base font-bold mb-3 tracking-tight">Centros con más interacciones</h4>
          {(data.top_orgs || []).length === 0 && (
            <div className="text-sm text-[var(--ag-text-dim)]">Ningún centro registró conversaciones.</div>
          )}
          <div className="space-y-1.5" data-testid="ai-insights-top-orgs">
            {(data.top_orgs || []).map((o) => (
              <Link key={o.org_id} to={`/reservar/${o.slug}`} target="_blank" rel="noopener" className="flex items-center justify-between text-sm py-1.5 px-2 rounded-sm hover:bg-[var(--ag-midnight)]" data-testid={`top-org-${o.slug}`}>
                <span className="text-[var(--ag-text)] truncate">{o.name}</span>
                <span className="font-mono tabular-nums text-[var(--ag-gold)] text-xs">{o.msgs} msgs</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="ag-card p-4 flex items-center gap-3 text-xs text-[var(--ag-text-dim)]">
        <Sparkle size={14} className="text-[var(--ag-gold)]" />
        <span>De los <b className="text-[var(--ag-text)]">{T.handoffs}</b> handoffs IA→reserva, <b className="text-[var(--ag-text)]">{T.handoffs_with_slot}</b> incluyen un slot sugerido. Reservas con `source=chat` registradas: <b className="text-[var(--ag-text)]">{T.bookings_from_chat}</b>.</span>
      </div>
    </div>
  );
}

// =======================================================================
// Sprint S-6 · Churn Risk tab
// =======================================================================
function ChurnRiskTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | high | medium

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/superadmin/churn-risk`);
      setData(r.data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const items = (data?.items || []).filter((r) => filter === "all" || r.level === filter);
  const counts = (data?.items || []).reduce((a, r) => { a[r.level] = (a[r.level] || 0) + 1; return a; }, {});

  return (
    <div className="space-y-5" data-testid="churn-risk-tab">
      <div className="ag-card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Pause size={18} weight="duotone" className="text-amber-400" />
            <h3 className="font-display text-xl font-bold tracking-tighter">Riesgo de churn</h3>
          </div>
          <button onClick={load} className="h-9 px-3 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] text-xs text-[var(--ag-text)] hover:bg-[var(--ag-midnight-soft)]" data-testid="churn-refresh">
            Recalcular
          </button>
        </div>
        <p className="text-xs text-[var(--ag-text-dim)] mb-4">
          Detector heurístico. Umbrales: ≥14 días sin reservas + caída &gt;50% últimos 30 d vs anteriores.
          Score ≥70 = alto (rojo), 40-69 = medio (ámbar). Las orgs creadas hace &lt;14 días no se evalúan.
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          {["all", "high", "medium"].map((k) => (
            <button key={k} onClick={()=>setFilter(k)}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-wider rounded-sm border ${filter===k?"bg-[var(--ag-gold)] text-black border-transparent font-bold":"border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"}`}
              data-testid={`churn-filter-${k}`}>
              {k === "all" ? `Todos (${data?.items?.length || 0})` : k === "high" ? `Alto (${counts.high || 0})` : `Medio (${counts.medium || 0})`}
            </button>
          ))}
        </div>
      </div>

      <div className="ag-card overflow-x-auto" data-testid="churn-list">
        {loading && <div className="p-6 text-sm text-[var(--ag-text-dim)]">Calculando riesgos…</div>}
        {!loading && items.length === 0 && (
          <div className="p-6 text-sm text-[var(--ag-text-dim)] text-center">🎉 No hay orgs en riesgo para los filtros aplicados.</div>
        )}
        {!loading && items.length > 0 && (
          <table className="w-full text-xs">
            <thead className="bg-[var(--ag-midnight)] text-[var(--ag-text-dim)] text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Centro</th>
                <th className="text-left px-3 py-2">Nivel</th>
                <th className="text-left px-3 py-2">Score</th>
                <th className="text-left px-3 py-2">Razones</th>
                <th className="text-left px-3 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.org_id} className="border-t border-[var(--ag-border)] text-[var(--ag-text)]" data-testid={`churn-row-${r.slug || r.org_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-[10px] text-[var(--ag-text-dim)]">{r.city || "—"} · {r.subscription_status}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-bold ${r.level === "high" ? "bg-red-500/15 text-red-300 border border-red-500/30" : "bg-amber-500/15 text-amber-300 border border-amber-500/30"}`}>
                      {r.level === "high" ? "ALTO" : "MEDIO"}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono tabular-nums">{r.score}/100</td>
                  <td className="px-3 py-2">
                    <ul className="list-disc list-inside space-y-0.5 text-[var(--ag-text-dim)]">
                      {(r.reasons || []).map((rs, i) => <li key={i}>{rs}</li>)}
                    </ul>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap space-x-1">
                    {r.admin_email && (
                      <a href={`mailto:${r.admin_email}?subject=Echamos de menos a ${r.name} en AutoGlow`} className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-[var(--ag-gold)] text-black text-[10px] font-bold uppercase" data-testid={`churn-contact-${r.slug || r.org_id}`}>
                        <PaperPlaneTilt size={11} weight="fill" /> Contactar
                      </a>
                    )}
                    {r.slug && (
                      <Link to={`/reservar/${r.slug}`} target="_blank" rel="noopener" className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[var(--ag-border)] text-[10px] uppercase hover:bg-[var(--ag-midnight-soft)]" data-testid={`churn-view-${r.slug}`}>
                        Ver perfil
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// =======================================================================
// Sprint S-7 · SaaS Metrics tab
// =======================================================================
function SaasMetricsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/superadmin/saas-metrics`); setData(data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  if (loading) return <div className="p-6 text-sm text-[var(--ag-text-dim)]">Calculando métricas…</div>;
  if (!data) return null;
  const fmt = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
  const maxChart = Math.max(1, ...(data.chart_last_12m || []).map((p) => p.mrr));
  return (
    <div className="space-y-5" data-testid="saas-metrics-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="MRR (este mes)" value={fmt(data.mrr)} accent="text-[var(--ag-gold)]" icon={CurrencyEur} testid="metric-mrr" />
        <StatCard label="ARR (anual.)" value={fmt(data.arr)} icon={ChartLineUp} testid="metric-arr" />
        <StatCard label="ARPU" value={fmt(data.arpu)} icon={Users} testid="metric-arpu" />
        <StatCard label="LTV estimado" value={fmt(data.ltv)} icon={Sparkle} testid="metric-ltv" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Orgs activas" value={data.active_orgs} icon={Buildings} testid="metric-active" />
        <StatCard label="Facturas (mes)" value={data.invoices_this_month} icon={Receipt} testid="metric-invoices" />
        <StatCard label="Churn 30d" value={`${data.churn_rate_pct_30d}%`} accent="text-red-300" icon={Pause} testid="metric-churn" />
        <StatCard
          label="Crecimiento vs mes ant."
          value={data.growth_pct_vs_last_month != null ? `${data.growth_pct_vs_last_month >= 0 ? "+" : ""}${data.growth_pct_vs_last_month}%` : "—"}
          accent={data.growth_pct_vs_last_month >= 0 ? "text-emerald-400" : "text-red-300"}
          icon={Lightning}
          testid="metric-growth"
        />
      </div>
      <div className="ag-card p-5">
        <h4 className="font-display text-base font-bold mb-4 tracking-tight">MRR · últimos 12 meses</h4>
        {(data.chart_last_12m || []).length === 0 ? (
          <div className="text-sm text-[var(--ag-text-dim)]">Sin datos históricos.</div>
        ) : (
          <div className="grid grid-cols-12 gap-1.5 items-end h-40" data-testid="metrics-chart">
            {data.chart_last_12m.slice(-12).map((p) => (
              <div key={p.ym} className="flex flex-col items-center gap-1.5">
                <div className="flex-1 w-full bg-[var(--ag-midnight)] rounded-sm relative overflow-hidden flex items-end" title={`${p.ym}: ${fmt(p.mrr)}`}>
                  <div className="w-full bg-[var(--ag-gold)] transition-all" style={{ height: `${(p.mrr / maxChart) * 100}%` }} />
                </div>
                <div className="text-[9px] font-mono text-[var(--ag-text-dim)]">{p.ym.slice(5)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      {(data.plans || []).length > 0 && (
        <div className="ag-card p-5">
          <h4 className="font-display text-base font-bold mb-3 tracking-tight">Desglose por plan</h4>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">
              <tr><th className="text-left py-1.5">Plan</th><th className="text-right py-1.5">Facturas</th><th className="text-right py-1.5">Importe</th></tr>
            </thead>
            <tbody>
              {data.plans.map((p) => (
                <tr key={p.plan_id} className="border-t border-[var(--ag-border)] text-[var(--ag-text)]">
                  <td className="py-1.5">{p.plan_id}</td>
                  <td className="py-1.5 text-right tabular-nums">{p.invoices}</td>
                  <td className="py-1.5 text-right tabular-nums font-mono">{fmt(p.amount_eur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =======================================================================
// Sprint S-7 · Coupons tab
// =======================================================================
function CouponsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: "", type: "percent", value: 10, applies_to: "saas", description: "", expires_at: "", max_redemptions: "" });
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/superadmin/coupons`); setItems(data.items || []); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, value: Number(form.value), max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null, expires_at: form.expires_at || null };
      await api.post(`/superadmin/coupons`, payload);
      toast.success("Cupón creado");
      setForm({ ...form, code: "", value: 10 });
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
  };
  const toggle = async (c) => {
    try { await api.patch(`/superadmin/coupons/${c.id}`, { enabled: !c.enabled }); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const del = async (c) => {
    if (!window.confirm(`¿Borrar el cupón ${c.code}?`)) return;
    try { await api.delete(`/superadmin/coupons/${c.id}`); load(); toast.success("Eliminado"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-5" data-testid="coupons-tab">
      <form onSubmit={create} className="ag-card p-5 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="coupons-create-form">
        <Input placeholder="CÓDIGO" value={form.code} onChange={(e)=>setForm({...form, code: e.target.value.toUpperCase()})} className="md:col-span-1" data-testid="coupon-code" />
        <select value={form.type} onChange={(e)=>setForm({...form, type: e.target.value})} className="h-10 px-2 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm" data-testid="coupon-type">
          <option value="percent">% descuento</option>
          <option value="amount">€ descuento</option>
          <option value="trial_extend">+días trial</option>
        </select>
        <Input type="number" placeholder="Valor" value={form.value} onChange={(e)=>setForm({...form, value: e.target.value})} data-testid="coupon-value" />
        <select value={form.applies_to} onChange={(e)=>setForm({...form, applies_to: e.target.value})} className="h-10 px-2 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm" data-testid="coupon-applies">
          <option value="saas">SaaS</option>
          <option value="booking">Reservas</option>
          <option value="both">Ambos</option>
        </select>
        <Input placeholder="Descripción (opcional)" value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} className="md:col-span-2" data-testid="coupon-desc" />
        <Input type="date" placeholder="Caducidad" value={form.expires_at} onChange={(e)=>setForm({...form, expires_at: e.target.value})} data-testid="coupon-expires" />
        <Input type="number" placeholder="Max canjes" value={form.max_redemptions} onChange={(e)=>setForm({...form, max_redemptions: e.target.value})} data-testid="coupon-max" />
        <Button type="submit" className="ag-btn-gold md:col-span-2" data-testid="coupon-submit"><Plus size={14} className="mr-1.5" /> Crear cupón</Button>
      </form>
      <div className="ag-card overflow-x-auto" data-testid="coupons-list">
        {loading ? <div className="p-6 text-sm text-[var(--ag-text-dim)]">Cargando…</div> :
         items.length === 0 ? <div className="p-6 text-sm text-[var(--ag-text-dim)] text-center">Sin cupones aún.</div> :
        <table className="w-full text-xs">
          <thead className="bg-[var(--ag-midnight)] text-[var(--ag-text-dim)] text-[10px] uppercase tracking-wider">
            <tr><th className="text-left px-3 py-2">Código</th><th className="text-left px-3 py-2">Tipo</th><th className="text-left px-3 py-2">Valor</th><th className="text-left px-3 py-2">Ámbito</th><th className="text-left px-3 py-2">Caduca</th><th className="text-left px-3 py-2">Canjes</th><th className="text-left px-3 py-2">Estado</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-[var(--ag-border)] text-[var(--ag-text)]" data-testid={`coupon-row-${c.code}`}>
                <td className="px-3 py-2 font-mono">{c.code}</td>
                <td className="px-3 py-2">{c.type}</td>
                <td className="px-3 py-2 tabular-nums">{c.type === "percent" ? `${c.value}%` : c.type === "amount" ? `${c.value} €` : `+${c.value} días`}</td>
                <td className="px-3 py-2">{c.applies_to}</td>
                <td className="px-3 py-2 text-[var(--ag-text-dim)]">{c.expires_at || "—"}</td>
                <td className="px-3 py-2 tabular-nums">{c.redemptions}{c.max_redemptions ? `/${c.max_redemptions}` : ""}</td>
                <td className="px-3 py-2">
                  <button onClick={()=>toggle(c)} className={`px-2 py-0.5 rounded-sm text-[10px] uppercase font-bold ${c.enabled ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-zinc-500/15 text-zinc-400 border border-zinc-500/30"}`} data-testid={`coupon-toggle-${c.code}`}>
                    {c.enabled ? "Activo" : "Pausado"}
                  </button>
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={()=>del(c)} className="text-red-400 hover:text-red-300 p-1" data-testid={`coupon-del-${c.code}`}><Trash size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>}
      </div>
    </div>
  );
}

// =======================================================================
// Sprint S-7 · Affiliates tab
// =======================================================================
function AffiliatesTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState({ paid: "", month: "" });
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.paid !== "") params.set("paid", filter.paid);
      if (filter.month) params.set("month", filter.month);
      const { data } = await api.get(`/superadmin/affiliate-commissions?${params.toString()}`);
      setData(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.paid, filter.month]);
  const markPaid = async (id) => {
    try { await api.post(`/superadmin/affiliate-commissions/${id}/mark-paid`); load(); toast.success("Marcado como pagado"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const exportCsv = () => {
    const params = new URLSearchParams();
    if (filter.paid !== "") params.set("paid", filter.paid);
    if (filter.month) params.set("month", filter.month);
    window.open(`${BACKEND_URL}/api/superadmin/affiliate-commissions/export.csv?${params.toString()}`, "_blank");
  };
  const fmt = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);
  return (
    <div className="space-y-5" data-testid="affiliates-tab">
      <div className="ag-card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Users size={18} weight="duotone" className="text-[var(--ag-gold)]" />
            <h3 className="font-display text-xl font-bold tracking-tighter">Comisiones de afiliados</h3>
          </div>
          <Button onClick={exportCsv} variant="outline" className="h-9 border-[var(--ag-border)] text-[var(--ag-text)]" data-testid="affiliates-export-csv">
            <FileArrowDown size={14} className="mr-1.5" /> Export CSV
          </Button>
        </div>
        <p className="text-xs text-[var(--ag-text-dim)] mb-3">
          Modelo: 20 % MRR durante los 12 primeros meses por cada centro referido (opción A). Pagos mensuales manuales mediante export CSV.
        </p>
        {data?.summary && (
          <div className="flex gap-3 flex-wrap mb-3">
            <span className="px-3 py-1.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/30 text-xs uppercase tracking-wider" data-testid="aff-pending">
              Pendiente: <b className="ml-1 tabular-nums">{fmt(data.summary.pending_eur)}</b>
            </span>
            <span className="px-3 py-1.5 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-xs uppercase tracking-wider" data-testid="aff-paid">
              Pagado: <b className="ml-1 tabular-nums">{fmt(data.summary.paid_eur)}</b>
            </span>
          </div>
        )}
        <div className="flex flex-wrap gap-2 items-center">
          <select value={filter.paid} onChange={(e)=>setFilter({...filter, paid: e.target.value})} className="h-9 px-2 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] text-[var(--ag-text)] text-xs" data-testid="aff-filter-paid">
            <option value="">Todos</option>
            <option value="false">Solo pendientes</option>
            <option value="true">Solo pagados</option>
          </select>
          <input type="month" value={filter.month} onChange={(e)=>setFilter({...filter, month: e.target.value})} className="h-9 px-2 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] text-[var(--ag-text)] text-xs" data-testid="aff-filter-month" />
        </div>
      </div>
      <div className="ag-card overflow-x-auto" data-testid="affiliates-list">
        {loading ? <div className="p-6 text-sm text-[var(--ag-text-dim)]">Cargando…</div> :
         (data?.items || []).length === 0 ? <div className="p-6 text-sm text-[var(--ag-text-dim)] text-center">Sin comisiones para los filtros aplicados.</div> :
        <table className="w-full text-xs">
          <thead className="bg-[var(--ag-midnight)] text-[var(--ag-text-dim)] text-[10px] uppercase tracking-wider">
            <tr><th className="text-left px-3 py-2">Mes</th><th className="text-left px-3 py-2">Afiliado</th><th className="text-left px-3 py-2">Centro referido</th><th className="text-left px-3 py-2">Factura</th><th className="text-right px-3 py-2">Comisión</th><th className="text-left px-3 py-2">Estado</th><th></th></tr>
          </thead>
          <tbody>
            {data.items.map((c) => (
              <tr key={c.id} className="border-t border-[var(--ag-border)] text-[var(--ag-text)]" data-testid={`aff-row-${c.id}`}>
                <td className="px-3 py-2 font-mono">{c.month}</td>
                <td className="px-3 py-2"><div className="font-semibold">{c.affiliate_name || "—"}</div><div className="text-[10px] text-[var(--ag-text-dim)]">{c.affiliate_email}</div></td>
                <td className="px-3 py-2">{c.referred_org_name || "—"}</td>
                <td className="px-3 py-2 tabular-nums font-mono">{c.invoice_id} · {fmt(c.invoice_amount_eur)}</td>
                <td className="px-3 py-2 tabular-nums font-mono text-right text-[var(--ag-gold)] font-bold">{fmt(c.amount_eur)}</td>
                <td className="px-3 py-2">
                  {c.paid ? <span className="text-emerald-400">✓ Pagado</span> : <span className="text-amber-300">Pendiente</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  {!c.paid && <button onClick={()=>markPaid(c.id)} className="px-2 py-1 rounded-sm bg-[var(--ag-gold)] text-black text-[10px] font-bold uppercase" data-testid={`aff-mark-paid-${c.id}`}>Marcar pagado</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>}
      </div>
    </div>
  );
}

// =======================================================================
// Mega-Sprint B · North Star tab
// =======================================================================
function NorthStarTab() {
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const [{ data: ns }, { data: cfg }] = await Promise.all([
        api.get("/superadmin/north-star"),
        api.get("/superadmin/north-star/config"),
      ]);
      setData(ns); setConfig(cfg);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const changeKpi = async (newKpi) => {
    try {
      await api.post("/superadmin/north-star/config", { kpi: newKpi });
      toast.success("KPI actualizado");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  if (loading || !data) return <div className="p-6 text-sm text-[var(--ag-text-dim)]">Cargando…</div>;
  const maxV = Math.max(1, ...data.sparkline.map((p) => p.v));
  const KPI_LABELS = { mrr: "MRR", active_orgs: "Orgs activas", nps_avg: "NPS (30d)", chat_conv_rate: "Conv. chat IA", bookings_per_day: "Reservas/día" };
  return (
    <div className="space-y-5" data-testid="north-star-tab">
      <div className="ag-card p-6 bg-gradient-to-br from-[var(--ag-gold)]/10 to-transparent border-[var(--ag-gold)]/40">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Crown size={20} weight="fill" className="text-[var(--ag-gold)]" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-[var(--ag-gold)]">Estrella del Norte</span>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="north-star-picker">
            {(config?.options || []).map((k) => (
              <button key={k} onClick={()=>changeKpi(k)}
                className={`px-2.5 py-1 text-[10px] uppercase tracking-wider rounded-sm border ${config?.kpi === k ? "bg-[var(--ag-gold)] text-black border-transparent font-bold" : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"}`}
                data-testid={`ns-pick-${k}`}>
                {KPI_LABELS[k] || k}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-3 flex items-baseline gap-3">
          <div className="font-display font-black text-6xl sm:text-7xl tabular-nums text-[var(--ag-text)]" data-testid="north-star-value">
            {data.unit === "€" ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(data.value) :
             data.unit === "%" ? `${data.value}%` :
             data.value}
          </div>
          {data.trend_pct_7d != null && (
            <span className={`font-mono text-sm font-bold ${data.trend_pct_7d >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="north-star-trend">
              {data.trend_pct_7d >= 0 ? "▲" : "▼"} {Math.abs(data.trend_pct_7d)}%
            </span>
          )}
        </div>
        <div className="text-[var(--ag-text-dim)] mt-1 text-sm">{data.label}</div>
        {/* Sparkline */}
        <div className="mt-6 h-24 flex items-end gap-0.5" data-testid="north-star-sparkline">
          {data.sparkline.length === 0 ? (
            <div className="text-xs text-[var(--ag-text-dim)] mx-auto self-center">Sin datos en el periodo.</div>
          ) : (
            data.sparkline.map((p) => (
              <div key={p.d} className="flex-1 bg-[var(--ag-gold)]/70 hover:bg-[var(--ag-gold)] rounded-sm transition-colors min-w-[2px]" style={{ height: `${(p.v / maxV) * 100}%` }} title={`${p.d}: ${p.v}`} />
            ))
          )}
        </div>
      </div>
      <p className="text-xs text-[var(--ag-text-dim)]">
        Elige tu métrica norte y todo el equipo verá el mismo objetivo. Cambia entre opciones para ver al instante orgs activas, conversión chat IA, NPS o reservas/día.
      </p>
    </div>
  );
}

// =======================================================================
// Mega-Sprint B · NPS Dashboard global
// =======================================================================
function NpsDashboardTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/superadmin/nps?days=${days}`); setData(data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (loading || !data) return <div className="p-6 text-sm text-[var(--ag-text-dim)]">Cargando NPS…</div>;
  const max = Math.max(1, ...data.distribution.map((d) => d.count));

  return (
    <div className="space-y-5" data-testid="nps-dashboard-tab">
      <div className="ag-card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-display text-xl font-bold tracking-tighter">NPS · respuestas en {days} días</h3>
          <select value={days} onChange={(e)=>setDays(Number(e.target.value))} className="h-9 px-2 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm" data-testid="nps-days">
            <option value={30}>Últimos 30 d</option>
            <option value={90}>Últimos 90 d</option>
            <option value={180}>Últimos 180 d</option>
            <option value={365}>Último año</option>
          </select>
        </div>
        {data.total === 0 ? (
          <div className="p-6 text-sm text-[var(--ag-text-dim)] text-center">Aún no hay respuestas NPS en este rango.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="NPS Score" value={`${data.nps ?? "–"}`} accent="text-[var(--ag-gold)]" icon={Crown} testid="nps-score" />
              <StatCard label="Media" value={`${data.average ?? "–"}/10`} icon={ChartLineUp} testid="nps-avg" />
              <StatCard label="Respuestas" value={data.total} icon={Users} testid="nps-total" />
              <StatCard label="Promotores" value={`${data.buckets.promoters} (${data.total ? Math.round(data.buckets.promoters / data.total * 100) : 0}%)`} accent="text-emerald-400" icon={Sparkle} testid="nps-promoters" />
            </div>
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] mb-2">Distribución 0–10</div>
              <div className="grid grid-cols-11 gap-1.5 items-end h-28" data-testid="nps-distribution">
                {data.distribution.map((d) => {
                  const color = d.score <= 6 ? "bg-red-400/70" : d.score <= 8 ? "bg-amber-400/70" : "bg-emerald-400/70";
                  return (
                    <div key={d.score} className="flex flex-col items-center gap-1">
                      <div className={`w-full ${color} rounded-sm transition-all`} style={{ height: `${(d.count / max) * 100}%` }} title={`${d.score}: ${d.count} resp.`} />
                      <div className="text-[9px] font-mono text-[var(--ag-text-dim)]">{d.score}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
      {data.recent_comments?.length > 0 && (
        <div className="ag-card p-5">
          <h4 className="font-display text-base font-bold mb-3 tracking-tight">Últimos comentarios</h4>
          <div className="space-y-2.5">
            {data.recent_comments.map((c) => {
              const color = c.score <= 6 ? "border-red-500/40 bg-red-500/5" : c.score <= 8 ? "border-amber-500/40 bg-amber-500/5" : "border-emerald-500/40 bg-emerald-500/5";
              return (
                <div key={c.id} className={`rounded-sm border ${color} p-3`} data-testid={`nps-comment-${c.id}`}>
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] mb-1">
                    <span>{c.role || "—"} · {c.email || "anónimo"}</span>
                    <span className="font-mono text-[var(--ag-text)]">★ {c.score}/10</span>
                  </div>
                  <div className="text-sm text-[var(--ag-text)]">{c.comment}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// =======================================================================
// Mega-Sprint B · A/B test results
// =======================================================================
function AbResultsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/superadmin/ab-results"); setData(data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="p-6 text-sm text-[var(--ag-text-dim)]">Cargando…</div>;
  return (
    <div className="space-y-5" data-testid="ab-results-tab">
      <div className="ag-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl font-bold tracking-tighter">Resultados A/B</h3>
          <Button onClick={load} variant="outline" className="h-9 text-xs">Recargar</Button>
        </div>
        <p className="text-xs text-[var(--ag-text-dim)] mb-4">
          Cualquier componente del frontend puede solicitar una variante con <code className="text-[var(--ag-text)]">GET /api/ab/variant/&lt;experimento&gt;</code> y registrar la conversión con <code className="text-[var(--ag-text)]">POST /api/ab/track/&lt;experimento&gt;/conversion</code>.
        </p>
        {data.experiments.length === 0 ? (
          <div className="p-6 text-sm text-[var(--ag-text-dim)] text-center">Aún no hay experimentos activos.</div>
        ) : (
          <div className="space-y-5">
            {data.experiments.map((e) => (
              <div key={e.experiment} className="border border-[var(--ag-border)] rounded-sm p-4" data-testid={`ab-exp-${e.experiment}`}>
                <div className="font-mono text-sm font-bold text-[var(--ag-gold)] mb-2">{e.experiment}</div>
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">
                    <tr><th className="text-left py-1">Variante</th><th className="text-right py-1">Visitantes</th><th className="text-right py-1">Conversiones</th><th className="text-right py-1">CR</th><th></th></tr>
                  </thead>
                  <tbody>
                    {e.rows.map((r, i) => (
                      <tr key={r.variant} className={`border-t border-[var(--ag-border)] ${i === 0 ? "bg-[var(--ag-gold)]/5" : ""}`} data-testid={`ab-row-${e.experiment}-${r.variant}`}>
                        <td className="py-1.5 font-mono">{i === 0 && <Crown size={11} weight="fill" className="inline text-[var(--ag-gold)] mr-1" />}{r.variant}</td>
                        <td className="py-1.5 text-right tabular-nums">{r.visitors}</td>
                        <td className="py-1.5 text-right tabular-nums">{r.conversions}</td>
                        <td className="py-1.5 text-right tabular-nums font-bold text-[var(--ag-text)]">{r.conversion_rate_pct}%</td>
                        <td className="py-1.5 text-right text-[10px] text-[var(--ag-text-dim)]">{i === 0 && e.rows.length > 1 ? "Mejor" : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =======================================================================
// Backlog · Tab Roadmap (lee /app/memory/BACKLOG.md)
// =======================================================================
function RoadmapTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending | doing | done
  const [prio, setPrio] = useState("all");      // all | P0 | P1 | P2 | P3

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/superadmin/roadmap"); setData(data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Error cargando roadmap"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const cycleState = async (code, current) => {
    const next = current === "pending" ? "doing" : current === "doing" ? "done" : "pending";
    try {
      await api.post(`/superadmin/roadmap/${code}`, { state: next });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  if (loading || !data) return <div className="p-6 text-sm text-[var(--ag-text-dim)]">Cargando roadmap…</div>;

  const visibleSections = data.sections
    .filter((s) => prio === "all" || s.priority === prio)
    .map((s) => ({ ...s, items: s.items.filter((it) => filter === "all" || it.state === filter) }))
    .filter((s) => s.items.length > 0);

  return (
    <div className="space-y-5" data-testid="roadmap-tab">
      <div className="ag-card p-6 bg-gradient-to-br from-[var(--ag-gold)]/10 to-transparent border-[var(--ag-gold)]/30">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ag-gold)]">Backlog oficial</div>
            <h3 className="font-display font-black text-2xl tracking-tighter mt-1">Roadmap AutoGlow</h3>
            <p className="text-xs text-[var(--ag-text-dim)] mt-1">
              Fuente: <code className="text-[var(--ag-text)]">/app/memory/BACKLOG.md</code>. Para añadir items edita el fichero y recarga.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Progreso</div>
            <div className="font-display font-black text-4xl tabular-nums text-[var(--ag-gold)]" data-testid="roadmap-percent">{data.totals.percent_done}%</div>
            <div className="text-[11px] text-[var(--ag-text-dim)] mt-1">
              <span className="text-emerald-400">{data.totals.done} hechos</span> ·
              <span className="text-amber-300 ml-1">{data.totals.doing} en curso</span> ·
              <span className="ml-1">{data.totals.pending} pendientes</span> ·
              <span className="ml-1">{data.totals.total} totales</span>
            </div>
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-2 rounded-sm bg-[var(--ag-midnight)] overflow-hidden flex" data-testid="roadmap-progress">
          <div className="bg-emerald-400" style={{ width: `${data.totals.done / data.totals.total * 100}%` }} />
          <div className="bg-amber-400" style={{ width: `${data.totals.doing / data.totals.total * 100}%` }} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="roadmap-filters">
        <div className="flex gap-1">
          {[["all","Todos"],["pending","Pendientes"],["doing","En curso"],["done","Hechos"]].map(([k, l]) => (
            <button key={k} onClick={()=>setFilter(k)}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-wider rounded-sm border ${filter===k?"bg-[var(--ag-gold)] text-black border-transparent font-bold":"border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"}`}
              data-testid={`roadmap-filter-${k}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex gap-1 ml-auto">
          {["all","P0","P1","P2","P3"].map((p) => (
            <button key={p} onClick={()=>setPrio(p)}
              className={`px-2.5 py-1.5 text-[11px] font-mono uppercase rounded-sm border ${prio===p?"bg-white text-black border-transparent font-bold":"border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"}`}
              data-testid={`roadmap-prio-${p}`}>
              {p === "all" ? "Toda prioridad" : p}
            </button>
          ))}
        </div>
        <button onClick={load} className="px-3 py-1.5 text-[11px] uppercase tracking-wider border border-[var(--ag-border)] rounded-sm text-[var(--ag-text-dim)] hover:text-white" data-testid="roadmap-reload">
          ↻ Recargar
        </button>
        <button
          onClick={async () => {
            if (!window.confirm("Marcar como done los items completados en preview (B-002, B-004 a B-009)? Operación idempotente.")) return;
            try {
              const { data } = await api.post("/superadmin/roadmap/sync-completed", {});
              toast.success(`Sincronizado · ${data.updated.length} items marcados como done`);
              load();
            } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Error sincronizando"); }
          }}
          className="px-3 py-1.5 text-[11px] uppercase tracking-wider border border-emerald-500/40 bg-emerald-500/10 rounded-sm text-emerald-300 hover:bg-emerald-500/20"
          data-testid="roadmap-sync-completed"
          title="Sólo necesario una vez tras desplegar a producción."
        >
          ✓ Sync completados
        </button>
        <button
          onClick={async () => {
            if (!window.confirm("Ocultar del marketplace todas las orgs demo/seed (Lavadero Demo SL, CatSeedOrg, Repeat Wash Co, etc.)? Sólo necesario una vez tras el primer deploy a producción.")) return;
            try {
              const { data } = await api.post("/superadmin/orgs/mark-seed-demo");
              toast.success(`Ocultadas ${data.marked} orgs demo del marketplace`);
            } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Error marcando demos"); }
          }}
          className="px-3 py-1.5 text-[11px] uppercase tracking-wider border border-amber-500/40 bg-amber-500/10 rounded-sm text-amber-300 hover:bg-amber-500/20"
          data-testid="roadmap-mark-demos"
          title="Marca como is_demo=true las orgs de demo conocidas para ocultarlas del Marketplace público."
        >
          ⓘ Ocultar orgs demo
        </button>
      </div>

      {visibleSections.length === 0 ? (
        <div className="ag-card p-12 text-center text-sm text-[var(--ag-text-dim)]">Sin items para los filtros aplicados.</div>
      ) : (
        visibleSections.map((s) => (
          <div key={s.title} className="ag-card p-5" data-testid={`roadmap-section-${s.priority}`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display font-bold text-base tracking-tight">{s.title}</h4>
              <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-bold border ${
                s.priority === "P0" ? "bg-red-500/15 text-red-300 border-red-500/40" :
                s.priority === "P1" ? "bg-amber-500/15 text-amber-300 border-amber-500/40" :
                s.priority === "P2" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" :
                "bg-zinc-500/15 text-zinc-300 border-zinc-500/40"
              }`}>{s.priority}</span>
            </div>
            <ul className="space-y-1.5">
              {s.items.map((it) => (
                <li key={it.code} className="flex items-start gap-3 group" data-testid={`roadmap-item-${it.code}`}>
                  <button
                    onClick={() => cycleState(it.code, it.state)}
                    className={`mt-0.5 w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                      it.state === "done" ? "bg-emerald-500 border-emerald-500" :
                      it.state === "doing" ? "bg-amber-400 border-amber-400" :
                      "border-[var(--ag-border)] hover:border-[var(--ag-gold)]"
                    }`}
                    title={it.state === "done" ? "Click → pendiente" : it.state === "doing" ? "Click → hecho" : "Click → en curso"}
                    data-testid={`roadmap-check-${it.code}`}
                  >
                    {it.state === "done" && <span className="text-black text-xs font-black">✓</span>}
                    {it.state === "doing" && <span className="text-black text-[10px] font-black">·</span>}
                  </button>
                  <div className="flex-1 min-w-0">
                    <span className={`font-mono text-[10px] text-[var(--ag-gold)] mr-2`}>{it.code}</span>
                    <span className={`text-sm ${it.state === "done" ? "line-through text-[var(--ag-text-dim)]" : "text-[var(--ag-text)]"}`} dangerouslySetInnerHTML={{ __html: it.text.replace(/`([^`]+)`/g, '<code class="text-[var(--ag-gold)] bg-black/40 px-1 rounded text-[11px]">$1</code>') }} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}
